import { FC, useEffect, useMemo, useState } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import { CheckCircle as CheckCircleIcon } from "@material-ui/icons";
import { isEqual } from "lodash";
import { usePostHog } from "posthog-js/react";

import { Button, ErrorMessage, Modal, WarningMessage } from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { usePrevious } from "hooks";
import {
  formatAmount,
  formatCurrencyAmount,
  formatDate,
  formatNumber,
} from "lib/format";
import { parseNumber } from "lib/parse";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { getBuyNowPriceErrorMessage, setBuyNowPrice } from "services/listings";
import { BaseAmountSource, ResaleInfo } from "services/listings/types";
import { ListingOffer } from "services/offers/types";
import { Track } from "services/valuation/types";
import theme from "theme";
import { circleIconSize } from "theme/constants";

import SetBuyNowPriceForm, { FormValues } from "./SetBuyNowPriceForm";

interface SetBuyNowPriceModalProps {
  open: boolean;
  onClose: () => void;
  buyNowPrice: number;
  dollarAge: number;
  highestOpenBuyerOfferAmount: number;
  isAcceptingFinalOffersEnabled: boolean;
  isMarketplaceMedianDisabled: boolean;
  listingId: number;
  lte: number;
  ltm: number;
  marketplaceMedian: string | null;
  marketplaceMedianMultiplier: number;
  marketplaceMedianSource: BaseAmountSource;
  minimumPrice: number;
  offers: ListingOffer[];
  resaleInfo: ResaleInfo | null;
  royxTake: number;
  termExpirationDate: string | null;
  trackList: Track[];
}

const titleByStep = [
  "Set List Price",
  "Confirm List Price",
  "List Price Confirmed",
];

const initialSetBuyNowPriceValues = {
  saleProceeds: "0.00",
  buyNowPrice: "",
  royxTakeAmount: "0.00",
};

const SetBuyNowPriceModal: FC<SetBuyNowPriceModalProps> = ({
  open,
  onClose,
  buyNowPrice,
  dollarAge,
  highestOpenBuyerOfferAmount,
  isAcceptingFinalOffersEnabled,
  isMarketplaceMedianDisabled,
  listingId,
  lte,
  ltm,
  marketplaceMedian,
  marketplaceMedianMultiplier,
  marketplaceMedianSource,
  minimumPrice,
  offers,
  resaleInfo,
  royxTake,
  termExpirationDate,
  trackList,
}) => {
  const { handleRefreshListing, listing } = useAssetDetailData(listingId);
  const posthog = usePostHog();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(initialSetBuyNowPriceValues);
  const [isLoading, setIsLoading] = useState(false);
  const [statisticsChanged, setStatisticsChanged] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const statistics = useMemo(
    () => ({
      lte,
      tracklistLength: trackList.length,
      buyNowPrice,
      offers,
    }),
    [lte, trackList, buyNowPrice, offers],
  );
  const previousStatistics = usePrevious(statistics);

  useEffect(() => {
    if (!!previousStatistics && !isEqual(statistics, previousStatistics)) {
      setStatisticsChanged(true);
    }
  }, [statistics, previousStatistics]);

  const makeOnBackHandler = () => {
    if ([1].includes(step)) {
      return () => {
        setStep(0);
      };
    } else {
      return undefined;
    }
  };

  const handleSetBuyNowPrice = async () => {
    try {
      setIsLoading(true);
      await setBuyNowPrice(
        listingId,
        {
          buy_now_price: parseNumber(values.buyNowPrice),
        },
        posthog,
      );
      setStep(s => s + 1);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? getBuyNowPriceErrorMessage(error)
          : DEFAULT_ERROR_MESSAGE,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshListingWithLoadingState = async () => {
    try {
      setIsLoading(true);
      await handleRefreshListing();
      setStep(s => s + 1);
    } catch (e) {
      //pass
    } finally {
      setIsLoading(false);
    }
  };
  const handleSave = async (values: FormValues) => {
    setValues(values);
    setErrorMessage("");
    await handleRefreshListingWithLoadingState();
  };

  return (
    <Modal
      title={titleByStep[step]}
      open={open}
      onClose={onClose}
      onBack={makeOnBackHandler()}
    >
      {step === 0 && (
        <SetBuyNowPriceForm
          highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
          initialValues={values}
          isAcceptingFinalOffersEnabled={isAcceptingFinalOffersEnabled}
          isMarketplaceMedianDisabled={isMarketplaceMedianDisabled}
          listingKind={listing?.kind}
          marketplaceMedian={marketplaceMedian}
          marketplaceMedianMultiplier={marketplaceMedianMultiplier}
          marketplaceMedianSource={marketplaceMedianSource}
          minimumPrice={minimumPrice}
          onSave={handleSave}
          resaleInfo={resaleInfo}
          royxTake={royxTake}
        />
      )}
      {step === 1 && (
        <>
          <Typography component="div" gutterBottom>
            <Box fontSize={13} textAlign="center">
              Your listing will be immediately updated.
            </Box>
          </Typography>
          <Box m={2} textAlign="center">
            <Box fontSize={18} m={1}>
              List Price
            </Box>
            <Box
              fontSize={18}
              fontWeight={theme.typography.fontWeightMedium}
              m={1}
            >
              {formatCurrencyAmount("USD", parseNumber(values.buyNowPrice))}
            </Box>
          </Box>
          <Box m={2} textAlign="center">
            <Box fontSize={18} m={1}>
              Your {isAcceptingFinalOffersEnabled ? "Minimum " : ""}Sale
              Proceeds
            </Box>
            <Box
              fontSize={18}
              fontWeight={theme.typography.fontWeightMedium}
              m={1}
            >
              {formatCurrencyAmount("USD", parseNumber(values.saleProceeds))}
            </Box>
            {isAcceptingFinalOffersEnabled && (
              <Typography component="div">
                <Box fontSize={10} textAlign="center" m={1}>
                  The marketplace fee will be automatically subtracted from the
                  highest bid at the end of the auction.
                </Box>
              </Typography>
            )}
          </Box>
          <Divider />
          <Box m={2} textAlign="center">
            {statisticsChanged && (
              <WarningMessage
                title="Statistics Changed"
                description={
                  "The earning statistics have changed for " +
                  "this catalog. Please check to confirm before " +
                  "proceeding."
                }
              />
            )}
            {errorMessage && (
              <Box my={2}>
                <ErrorMessage
                  title="Request Failed"
                  description={errorMessage}
                />
              </Box>
            )}
            <Typography component="div">
              <Box fontSize={13} textAlign="center" m={2}>
                Confirmed Earning Statistics
              </Box>
              <Box m={2}>
                <Box fontSize={15}>Earns Through</Box>
                <Box
                  fontSize={15}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  {formatDate(termExpirationDate)}
                </Box>
              </Box>
              <Box m={2}>
                <Box fontSize={15}>Last 12 Months&apos; Earnings</Box>
                <Box
                  fontSize={15}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  {formatAmount(ltm)}
                </Box>
              </Box>
              <Box m={2}>
                <Box fontSize={15}>Dollar Age</Box>
                <Box
                  fontSize={15}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  {formatNumber(dollarAge, " Years")}
                </Box>
              </Box>
            </Typography>
          </Box>
          <Box m={2} textAlign="center">
            <Typography component="div">
              <Box fontSize={10} textAlign="center" m={1}>
                {isAcceptingFinalOffersEnabled
                  ? "By clicking below, you acknowledge that you are submitting a binding offer to sell the asset to the highest bidder at or above the list price. Your offer shall remain binding until changed or revoked by you."
                  : "By clicking below, you acknowledge that you are submitting a binding offer to sell the asset at the price submitted. Your offer shall remain binding until changed or revoked by you."}
              </Box>
            </Typography>
            <Box m={2}>
              <Button
                customColor="green"
                variant="contained"
                size="large"
                id="btn-confirm-modal"
                fullWidth
                onClick={handleSetBuyNowPrice}
                disabled={isLoading}
                isLoading={isLoading}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </>
      )}
      {step === 2 && (
        <>
          <Typography component="div">
            <Box textAlign="center">
              <CheckCircleIcon
                style={{
                  fontSize: circleIconSize.big,
                  color: theme.custom.palette.green.main,
                }}
              />
            </Box>
            <Box fontSize={15} textAlign="center" m={2}>
              List Price submitted.
            </Box>
          </Typography>
        </>
      )}
    </Modal>
  );
};

export default SetBuyNowPriceModal;
